/**
 * TestSiteAccessModal
 *
 * Mandatory security gate for all test-environment sites.
 * Shows a modal that blocks all interaction until the user enters the
 * correct date-based access key (format: MMDDYYYYtest).
 *
 * Persistence: once verified, stores a flag in localStorage so the modal
 * is skipped on subsequent visits during the same browser session.
 *
 * localStorage key: "testSiteAccessVerified"
 */

import { FC, useState, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import styled from "@mui/material/styles/styled";

/* ------------------------------------------------------------------ */
/* localStorage key used to persist the verified state                 */
/* ------------------------------------------------------------------ */
export const TEST_SITE_VERIFIED_KEY = "testSiteAccessVerified";

/* ------------------------------------------------------------------ */
/* Utility: generate the expected access key for the current date      */
/* Format: MMDDYYYYtest  (e.g. 05142026test for May 14 2026)          */
/* ------------------------------------------------------------------ */
export const getExpectedAccessKey = (): string => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const yyyy = String(now.getFullYear());
  return `${mm}${dd}${yyyy}test`;
};

/* ------------------------------------------------------------------ */
/* Utility: check whether the browser has already been verified        */
/* ------------------------------------------------------------------ */
export const isTestSiteVerified = (): boolean =>
  localStorage.getItem(TEST_SITE_VERIFIED_KEY) === "true";

/* ------------------------------------------------------------------ */
/* Styled dialog — no backdrop click / ESC to close                    */
/* ------------------------------------------------------------------ */
const AccessDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
    justifyContent: "center",
  },
  "& .MuiDialog-paper": {
    borderRadius: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    minWidth: 360,
  },
}));

/* ------------------------------------------------------------------ */
/* Component props                                                      */
/* ------------------------------------------------------------------ */
interface ITestSiteAccessModal {
  /** Whether the modal is currently visible */
  open: boolean;
  /** Called when the user successfully verifies the access key */
  onVerified: () => void;
}

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */
const TestSiteAccessModal: FC<ITestSiteAccessModal> = ({ open, onVerified }) => {
  const [accessKey, setAccessKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  /* Toggle password visibility */
  const handleToggleVisibility = useCallback(() => {
    setShowKey((prev) => !prev);
  }, []);

  /* Validate and process submission */
  const handleSubmit = useCallback(() => {
    /* Rule 1: empty field */
    if (!accessKey.trim()) {
      setValidationMsg("Test Site Access Key is required.");
      return;
    }

    /* Rule 2: wrong key */
    if (accessKey.trim().toLocaleLowerCase() !== getExpectedAccessKey().toLocaleLowerCase()) {
      setValidationMsg("Invalid Test Site Access Key.");
      return;
    }

    /* Success — persist verification and notify parent */
    localStorage.setItem(TEST_SITE_VERIFIED_KEY, "true");
    setValidationMsg("");
    setAccessKey("");
    onVerified();
  }, [accessKey, onVerified]);

  /* Allow Enter key to submit */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleSubmit();
    },
    [handleSubmit]
  );

  return (
    <AccessDialog
      open={open}
      /* disableEscapeKeyDown and no onClose prop make the modal non-dismissable */
      disableEscapeKeyDown
      aria-labelledby="test-site-access-title"
      /* Prevent backdrop click from closing */
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
    >
      {/* Header */}
      <DialogTitle
        id="test-site-access-title"
        sx={{
          background: "linear-gradient(135deg, #5acbff 0%, #d6f2ff 100%)",
          color: "#fff",
          textAlign: "center",
          py: 2.5,
          fontWeight: 700,
          fontSize: "1.1rem",
          letterSpacing: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "left", color: "#242424", gap: 1,
          fontSize:18 }}>
          <LockOutlinedIcon fontSize="small" />
          Test System Alert
        </Box>
      </DialogTitle>

      {/* Body */}
      <DialogContent>
        {/* Instruction message */}
        <Typography
          variant="body2"
          sx={{
            mb: 2.5,
            mt: 0.5,
            color: "#555",
            textAlign: "center",
            lineHeight: 1.6,
            marginTop: "10px",
          }}
        >
          Attention ! This is a Test System. For Production system, please visit the Production site.
        </Typography>

        {/* Password input with show/hide toggle */}
        <TextField
          label="Test Site Access Key"
          type={showKey ? "text" : "password"}
          value={accessKey}
          onChange={(e) => {
            setAccessKey(e.target.value);
            /* Clear validation on input change */
            if (validationMsg) setValidationMsg("");
          }}
          onKeyDown={handleKeyDown}
          fullWidth
          variant="outlined"
          size="small"
          autoComplete="off"
          error={!!validationMsg}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showKey ? "Hide access key" : "Show access key"}
                  onClick={handleToggleVisibility}
                  edge="end"
                  size="small"
                >
                  {showKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Validation message */}
        {validationMsg && (
          <Typography
            variant="caption"
            role="alert"
            sx={{ color: "#c0392b", mt: 1, display: "block", fontWeight: 500 }}
          >
            {validationMsg}
          </Typography>
        )}
      </DialogContent>

      {/* Submit button */}
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            background: "linear-gradient(135deg, #0f336d 0%, #0f336d 100%)",
            color: "#fff",
            px: 4,
            py: 1,
            borderRadius: 2,
            fontWeight: 600,
            "&:hover": {
              background: "linear-gradient(135deg, #0f336d 0%, #1976d2 100%)",
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </AccessDialog>
  );
};

export default TestSiteAccessModal;
